import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { 
  MessageSquare, 
  Search, 
  Plus, 
  Filter,
  Download,
  MoreVertical,
  Users,
  User,
  Bot,
  TrendingUp,
  Activity,
  Clock,
  Mail,
  Phone,
  AlertCircle,
  CheckCircle,
  Eye,
  Edit,
  Trash2,
  Reply,
  Forward,
  Star,
  Archive,
  Send,
  Paperclip,
  Smile,
  BarChart3,
  DollarSign,
  Target,
  Sparkles,
  Award,
  Calendar,
  Settings,
  Brain
} from 'lucide-react';
import { Button } from '../../components/ui/Button/Button';
import { cn } from '../../utils/cn';
import RevolutionaryConversationView from '../../components/conversations/revolutionary/RevolutionaryConversationView';

// Import new design system components
import { 
  ConversationLayout, 
  ConversationPanel, 
  PanelHeader, 
  PanelContent,
  // New primitive components for demonstration
  Button as NewButton,
  Card,
  Input,
  Badge,
  Avatar,
  Panel
} from '../../design-system';
import { ConversationCard as NewConversationCard } from '../../design-system/components/primitives/ConversationCard/ConversationCard';
import { useKeyboardShortcuts, useConversationShortcuts } from '../../design-system/hooks/useKeyboardShortcuts';
import { useFeatureFlag } from '../../hooks/useFeatureFlag';

// Conversation interface
interface Conversation {
  id: string;
  contact: {
    name: string;
    email: string;
    company?: string;
    avatar?: string;
  };
  lastMessage: {
    content: string;
    timestamp: string;
    isOutgoing: boolean;
  };
  status: 'active' | 'positive' | 'high-priority' | 'needs-response' | 'closed';
  priority: 'low' | 'medium' | 'high';
  campaign?: string;
  messageCount: number;
  unreadCount: number;
  labels: string[];
}

interface ConversationStats {
  totalConversations: number;
  activeConversations: number;
  highPriority: number;
  avgResponseTime: string;
  responseRate: number;
  positiveReplies: number;
}

// Animated counter component
const AnimatedCounter: React.FC<{ end: number; duration?: number; prefix?: string; suffix?: string }> = ({ 
  end, 
  duration = 2000, 
  prefix = "", 
  suffix = ""
}) => {
  const [count, setCount] = useState(0);

  useEffect(() => {
    let startTime: number;
    let animationFrame: number;

    const animate = (timestamp: number) => {
      if (!startTime) startTime = timestamp;
      const progress = Math.min((timestamp - startTime) / duration, 1);
      
      setCount(Math.floor(progress * end));
      
      if (progress < 1) {
        animationFrame = requestAnimationFrame(animate);
      }
    };

    animationFrame = requestAnimationFrame(animate);
    
    return () => cancelAnimationFrame(animationFrame);
  }, [end, duration]);

  return (
    <span className="font-bold">
      {prefix}{count.toLocaleString()}{suffix}
    </span>
  );
};

// Metric Card Component
const MetricCard: React.FC<{
  title: string;
  value: string | number;
  change: string;
  icon: React.ElementType;
  gradient: string;
  description?: string;
}> = ({ title, value, change, icon: Icon, gradient, description }) => {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      whileHover={{ y: -4, scale: 1.02 }}
      className="group relative p-6 bg-gradient-to-br from-neutral-900/80 to-neutral-800/50 backdrop-blur-xl border border-neutral-700/50 rounded-2xl hover:border-neutral-600/50 transition-all duration-300 shadow-xl hover:shadow-2xl"
    >
      <div className="flex items-start justify-between">
        <div>
          <p className="text-neutral-400 text-sm font-medium mb-2">{title}</p>
          <p className="text-3xl font-bold text-white mb-1">
            {typeof value === 'number' ? (
              <AnimatedCounter end={value} />
            ) : (
              value
            )}
          </p>
          <div className="flex items-center space-x-2">
            <span className="text-green-400 text-sm font-medium">{change}</span>
            <TrendingUp className="w-4 h-4 text-green-400" />
          </div>
          {description && (
            <p className="text-neutral-500 text-xs mt-2">{description}</p>
          )}
        </div>
        <div className={cn("w-12 h-12 rounded-xl flex items-center justify-center", gradient)}>
          <Icon className="w-6 h-6 text-white" />
        </div>
      </div>
      
      {/* Hover effect overlay */}
      <div className="absolute inset-0 bg-gradient-to-r from-blue-500/5 to-purple-500/5 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
    </motion.div>
  );
};

// Conversation Card Component
const ConversationCard: React.FC<{
  conversation: Conversation;
  isSelected: boolean;
  onClick: () => void;
  onRevolutionaryClick?: () => void;
  bulkSelectMode?: boolean;
  isBulkSelected?: boolean;
  onBulkSelect?: (id: string, selected: boolean) => void;
}> = ({ conversation, isSelected, onClick, onRevolutionaryClick, bulkSelectMode, isBulkSelected, onBulkSelect }) => {
  const getStatusConfig = (status: string) => {
    switch (status) {
      case 'active':
        return {
          color: 'text-green-400 bg-green-500/20 border-green-500/30',
          dot: 'bg-green-400',
          label: 'Active'
        };
      case 'positive':
        return {
          color: 'text-blue-400 bg-blue-500/20 border-blue-500/30',
          dot: 'bg-blue-400',
          label: 'Positive'
        };
      case 'high-priority':
        return {
          color: 'text-red-400 bg-red-500/20 border-red-500/30',
          dot: 'bg-red-400',
          label: 'High Priority'
        };
      case 'needs-response':
        return {
          color: 'text-orange-400 bg-orange-500/20 border-orange-500/30',
          dot: 'bg-orange-400',
          label: 'Needs Response'
        };
      default:
        return {
          color: 'text-neutral-400 bg-neutral-500/20 border-neutral-500/30',
          dot: 'bg-neutral-400',
          label: 'Closed'
        };
    }
  };

  const statusConfig = getStatusConfig(conversation.status);
  const initials = (conversation.contact?.name || 'U')
    .split(' ')
    .map(n => n.charAt(0))
    .join('')
    .substring(0, 2)
    .toUpperCase();

  return (
    <motion.div
      initial={{ opacity: 0, x: -20 }}
      animate={{ opacity: 1, x: 0 }}
      whileHover={{ x: 4 }}
      onClick={bulkSelectMode ? undefined : onClick}
      className={cn(
        "p-4 border-b border-neutral-700/50 cursor-pointer transition-all duration-200 hover:bg-neutral-800/30 relative",
        isSelected && !bulkSelectMode && "bg-blue-500/10 border-blue-500/30",
        isBulkSelected && "bg-purple-500/10 border-purple-500/30"
      )}
    >
      {/* Bulk Selection Overlay */}
      {bulkSelectMode && (
        <div className="absolute inset-0 bg-neutral-800/50 backdrop-blur-sm flex items-center justify-center z-10">
          <input
            type="checkbox"
            checked={isBulkSelected}
            onChange={(e) => onBulkSelect?.(conversation.id, e.target.checked)}
            className="w-5 h-5 accent-purple-500"
          />
        </div>
      )}

      <div className="flex items-start space-x-4">
        {/* Avatar */}
        <div className="relative flex-shrink-0">
          <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full flex items-center justify-center">
            <span className="text-white text-sm font-semibold">{initials}</span>
          </div>
          <div className={cn("absolute -bottom-1 -right-1 w-4 h-4 rounded-full border-2 border-neutral-900", statusConfig.dot)} />
          
          {/* Real-time activity indicator */}
          {Math.random() > 0.7 && (
            <div className="absolute -top-1 -right-1 w-3 h-3 bg-green-400 rounded-full animate-pulse border border-neutral-900"></div>
          )}
        </div>

        {/* Content */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between mb-1">
            <h3 className="font-medium text-white truncate">{conversation.contact?.name || 'Unknown Contact'}</h3>
            <span className="text-xs text-neutral-400">
              {conversation.lastMessage?.timestamp ? new Date(conversation.lastMessage.timestamp).toLocaleDateString() : 'No date'}
            </span>
          </div>

          <div className="flex items-center space-x-2 mb-2">
            <span className={cn(
              "inline-flex items-center px-2 py-1 rounded-full text-xs font-medium border backdrop-blur-xl",
              statusConfig.color
            )}>
              <div className="w-2 h-2 rounded-full bg-current mr-1" />
              {statusConfig.label}
            </span>
            
            {conversation.campaign && (
              <span className="px-2 py-1 bg-neutral-700/50 text-neutral-300 text-xs rounded-full">
                {conversation.campaign}
              </span>
            )}
          </div>

          <p className="text-sm text-neutral-400 line-clamp-2 mb-2">
            {conversation.lastMessage?.content?.includes('Email sent via workflow') ? (
              <span 
                onClick={(e) => {
                  e.stopPropagation();
                  onRevolutionaryClick?.();
                }}
                className="text-blue-400 hover:text-blue-300 cursor-pointer underline hover:bg-blue-500/10 px-1 py-0.5 rounded transition-all duration-200"
              >
                {conversation.lastMessage.content}
              </span>
            ) : (
              conversation.lastMessage?.content || 'No message content'
            )}
          </p>

          <div className="flex items-center justify-between text-xs text-neutral-500">
            <span>{conversation.messageCount} messages</span>
            {conversation.unreadCount > 0 && (
              <span className="px-2 py-1 bg-blue-500 text-white rounded-full">
                {conversation.unreadCount}
              </span>
            )}
          </div>
        </div>
      </div>
    </motion.div>
  );
};

// Conversation Detail View Component
const ConversationDetailView: React.FC<{ 
  conversation: Conversation;
  onRevolutionaryClick?: () => void;
}> = ({ conversation, onRevolutionaryClick }) => {
  const [conversationDetails, setConversationDetails] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [newMessage, setNewMessage] = useState('');

  // Load conversation details with messages
  useEffect(() => {
    const loadConversationDetails = async () => {
      setLoading(true);
      try {
        const response = await fetch(`/api/conversations/${conversation.id}`);
        const result = await response.json();
        
        if (result.success) {
          setConversationDetails(result.data);
        }
      } catch (error) {
        console.error('Failed to load conversation details:', error);
      } finally {
        setLoading(false);
      }
    };

    loadConversationDetails();
  }, [conversation.id]);

  const handleSendMessage = async () => {
    if (!newMessage.trim()) return;
    
    // TODO: Implement message sending
    console.log('Sending message:', newMessage);
    setNewMessage('');
  };

  if (loading) {
    return (
      <div className="h-full flex items-center justify-center">
        <div className="text-center">
          <div className="w-8 h-8 border-4 border-blue-500/30 border-t-blue-500 rounded-full animate-spin mx-auto mb-3"></div>
          <p className="text-neutral-400 text-sm">Loading conversation...</p>
        </div>
      </div>
    );
  }

  const getStatusConfig = (status: string) => {
    switch (status) {
      case 'active':
        return { color: 'text-green-400 bg-green-500/20 border-green-500/30', label: 'Active' };
      case 'positive':
        return { color: 'text-blue-400 bg-blue-500/20 border-blue-500/30', label: 'Positive' };
      case 'high-priority':
        return { color: 'text-red-400 bg-red-500/20 border-red-500/30', label: 'High Priority' };
      case 'needs-response':
        return { color: 'text-orange-400 bg-orange-500/20 border-orange-500/30', label: 'Needs Response' };
      case 'closed':
        return { color: 'text-neutral-400 bg-neutral-500/20 border-neutral-500/30', label: 'Closed' };
      default:
        return { color: 'text-neutral-400 bg-neutral-500/20 border-neutral-500/30', label: 'Unknown' };
    }
  };

  const statusConfig = getStatusConfig(conversationDetails?.status || 'active');

  return (
    <div className="h-full flex flex-col">
      {/* Conversation Header */}
      <div className="p-6 border-b border-neutral-700/50">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full flex items-center justify-center">
              <span className="text-white text-sm font-semibold">
                {(conversation.contact?.name || 'U').split(' ').map(n => n.charAt(0)).join('').substring(0, 2).toUpperCase()}
              </span>
            </div>
            <div>
              <h3 className="text-lg font-semibold text-white">{conversation.contact?.name || 'Unknown Contact'}</h3>
              <p className="text-neutral-400 text-sm">{conversation.contact?.email || 'No email'}</p>
              {conversation.contact?.company && (
                <p className="text-neutral-500 text-xs">{conversation.contact.company}</p>
              )}
            </div>
          </div>
          
          <div className="flex items-center space-x-3">
            {/* Status Badge */}
            <span className={cn(
              "inline-flex items-center px-3 py-1 rounded-full text-xs font-medium border backdrop-blur-xl",
              statusConfig.color
            )}>
              <div className="w-2 h-2 rounded-full bg-current mr-1" />
              {statusConfig.label}
            </span>

            {/* Action Buttons */}
            <div className="flex space-x-2">
              <Button 
                onClick={onRevolutionaryClick}
                className="bg-gradient-to-r from-purple-500 to-blue-600 hover:from-purple-600 hover:to-blue-700 text-white border-0"
                size="sm"
              >
                <Sparkles className="w-4 h-4 mr-1" />
                Revolutionary
              </Button>
              <Button variant="outline" size="sm" className="border-neutral-600 text-neutral-300">
                <Star className="w-4 h-4" />
              </Button>
              <Button variant="outline" size="sm" className="border-neutral-600 text-neutral-300">
                <Archive className="w-4 h-4" />
              </Button>
              <Button variant="outline" size="sm" className="border-neutral-600 text-neutral-300">
                <MoreVertical className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </div>

        {/* Campaign Info */}
        {conversation.campaign && (
          <div className="mt-3 flex items-center space-x-2">
            <span className="px-2 py-1 bg-blue-500/20 text-blue-300 text-xs rounded-full border border-blue-500/30">
              {conversation.campaign}
            </span>
            <span className="text-neutral-500 text-xs">
              {conversationDetails?.messageCount || 0} messages
            </span>
          </div>
        )}
      </div>

      {/* Messages Area */}
      <div className="flex-1 overflow-y-auto p-6 space-y-4">
        {conversationDetails?.messages && conversationDetails.messages.length > 0 ? (
          conversationDetails.messages.map((message: any, index: number) => (
            <motion.div
              key={message.id || index}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1 }}
              className={cn(
                "flex",
                message.isOutgoing ? "justify-end" : "justify-start"
              )}
            >
              <div className={cn(
                "max-w-xs lg:max-w-md px-4 py-3 rounded-2xl shadow-lg",
                message.isOutgoing
                  ? "bg-gradient-to-r from-blue-500 to-purple-600 text-white"
                  : "bg-gradient-to-r from-neutral-800 to-neutral-700 text-white border border-neutral-600/50"
              )}>
                <div className="flex items-start space-x-2">
                  {!message.isOutgoing && (
                    <User className="w-4 h-4 mt-1 text-neutral-400 flex-shrink-0" />
                  )}
                  {message.isOutgoing && (
                    <Bot className="w-4 h-4 mt-1 text-blue-200 flex-shrink-0" />
                  )}
                  <div className="flex-1">
                    <p className="text-sm leading-relaxed">{message.content}</p>
                    
                    {/* Message metadata */}
                    <div className="flex items-center justify-between mt-2">
                      <span className={cn(
                        "text-xs",
                        message.isOutgoing ? "text-blue-100" : "text-neutral-400"
                      )}>
                        {new Date(message.timestamp).toLocaleTimeString()}
                      </span>
                      
                      {message.channel && (
                        <span className={cn(
                          "text-xs px-2 py-1 rounded-full",
                          message.isOutgoing ? "bg-blue-600/50" : "bg-neutral-700/50"
                        )}>
                          {message.channel}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          ))
        ) : (
          <div className="text-center py-12">
            <MessageSquare className="w-12 h-12 text-neutral-600 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-white mb-2">No messages yet</h3>
            <p className="text-neutral-400 text-sm">
              Start the conversation by sending a message below
            </p>
          </div>
        )}
      </div>

      {/* Message Composer */}
      <div className="p-6 border-t border-neutral-700/50 bg-gradient-to-r from-neutral-900/50 to-neutral-800/50">
        <div className="flex items-center space-x-3">
          <div className="flex-1 relative">
            <textarea
              value={newMessage}
              onChange={(e) => setNewMessage(e.target.value)}
              onKeyPress={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  handleSendMessage();
                }
              }}
              placeholder="Type your message..."
              className="w-full px-4 py-3 bg-neutral-800/50 border border-neutral-700 rounded-xl text-white placeholder-neutral-400 focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-transparent resize-none"
              rows={2}
            />
          </div>
          <Button
            onClick={handleSendMessage}
            disabled={!newMessage.trim()}
            className="bg-gradient-to-r from-blue-500 to-purple-600 hover:from-blue-600 hover:to-purple-700 px-6"
          >
            <Send className="w-4 h-4" />
          </Button>
        </div>
      </div>
    </div>
  );
};

interface Campaign {
  id: string;
  name: string;
  subject: string;
  status: string;
}

export const ConversationsPage: React.FC = () => {
  const navigate = useNavigate();
  
  // Feature flags
  const isRevolutionaryEnabled = useFeatureFlag('conversations-v2');
  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [campaigns, setCampaigns] = useState<Campaign[]>([]);
  const [selectedConversation, setSelectedConversation] = useState<Conversation | null>(null);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('All');
  const [campaignFilter, setCampaignFilter] = useState<string>('All');
  const [showRevolutionaryMode, setShowRevolutionaryMode] = useState(false);
  const [showCommandPalette, setShowCommandPalette] = useState(false);
  const [bulkSelectMode, setBulkSelectMode] = useState(false);
  const [selectedConversations, setSelectedConversations] = useState<Set<string>>(new Set());
  const [realTimeUpdates, setRealTimeUpdates] = useState(true);
  const [stats, setStats] = useState<ConversationStats>({
    totalConversations: 3,
    activeConversations: 2,
    highPriority: 1,
    avgResponseTime: '45h',
    responseRate: 85,
    positiveReplies: 12
  });

  // Command Palette functionality
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        setShowCommandPalette(true);
      }
      if (e.key === 'Escape') {
        setShowCommandPalette(false);
        setBulkSelectMode(false);
        setSelectedConversations(new Set());
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  // Real-time updates simulation
  useEffect(() => {
    if (!realTimeUpdates) return;
    
    const interval = setInterval(() => {
      setStats(prev => ({
        ...prev,
        activeConversations: prev.activeConversations + Math.floor(Math.random() * 2) - 1,
        avgResponseTime: `${Math.floor(Math.random() * 60)}m`
      }));
    }, 5000);

    return () => clearInterval(interval);
  }, [realTimeUpdates]);

  // Enhanced data loading with real-time capabilities
  const loadConversations = async () => {
    setLoading(true);
    try {
      // Build comprehensive query parameters
      const params = new URLSearchParams();
      if (campaignFilter !== 'All') params.append('campaign_id', campaignFilter);
      if (statusFilter !== 'All') params.append('status', statusFilter.toLowerCase().replace(' ', '_'));
      if (searchQuery) params.append('search', searchQuery);
      params.append('include_stats', 'true');
      params.append('include_ai_insights', 'true');
      params.append('real_time', realTimeUpdates.toString());
      
      // Call the enhanced conversations API
      const response = await fetch(`http://localhost:3001/api/conversations?${params}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (response.ok) {
      const result = await response.json();
      
      if (result.success && result.data) {
        setConversations(result.data.conversations || []);
        
          // Update comprehensive stats
        if (result.data.stats) {
          setStats({
              totalConversations: result.data.stats.totalConversations || 0,
              activeConversations: result.data.stats.activeConversations || 0,
              highPriority: result.data.stats.highPriority || 0,
            avgResponseTime: result.data.stats.avgResponseTime || '0s',
            responseRate: result.data.stats.responseRate || 0,
            positiveReplies: result.data.stats.positiveReplies || 0
          });
        }

          // Auto-select first conversation if none selected
          if (result.data.conversations.length > 0 && !selectedConversation) {
          setSelectedConversation(result.data.conversations[0]);
          }
        }
      } else {
        console.log('API not available, using enhanced demo data');
        // Enhanced demo data with more realistic conversations
        const demoConversations = [
          {
            id: 'conv-1',
            contact: {
              name: 'Andrewjohnvercueil',
              email: 'andrewjohnvercueil@gmail.com',
              company: 'TechCorp Inc.'
            },
            lastMessage: {
              content: 'Email sent via workflow: New Workflow',
              timestamp: new Date().toISOString(),
              isOutgoing: true
            },
            status: 'active',
            priority: 'high',
            campaign: 'Campaign 4f432e57',
            messageCount: 3,
            unreadCount: 1,
            labels: ['workflow', 'ai-generated', 'priority']
          },
          {
            id: 'conv-2',
            contact: {
              name: 'Sarah Johnson',
              email: 'sarah.johnson@example.com',
              company: 'StartupCo'
            },
            lastMessage: {
              content: 'Thanks for the quick response! When can we schedule a demo?',
              timestamp: new Date(Date.now() - 30000).toISOString(),
              isOutgoing: false
            },
            status: 'positive',
            priority: 'medium',
            campaign: 'Demo Requests',
            messageCount: 5,
            unreadCount: 0,
            labels: ['demo', 'interested']
          },
          {
            id: 'conv-3',
            contact: {
              name: 'Mike Chen',
              email: 'mike@enterprise.com',
              company: 'Enterprise Corp'
            },
            lastMessage: {
              content: 'I need to discuss pricing for enterprise features',
              timestamp: new Date(Date.now() - 120000).toISOString(),
              isOutgoing: false
            },
            status: 'needs-response',
            priority: 'high',
            campaign: 'Enterprise Sales',
            messageCount: 8,
            unreadCount: 2,
            labels: ['enterprise', 'pricing', 'urgent']
          }
        ];
        
        setConversations(demoConversations);
        setStats({
          totalConversations: 3,
          activeConversations: 2,
          highPriority: 2,
          avgResponseTime: '2m',
          responseRate: 95,
          positiveReplies: 12
        });
        
        if (!selectedConversation) {
          setSelectedConversation(demoConversations[0]);
        }
      }
    } catch (error) {
      console.error('Failed to load conversations:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadCampaigns = async () => {
    try {
      const response = await fetch('/api/campaigns');
      const result = await response.json();
      
      if (result.success) {
        setCampaigns(result.data || []);
      }
    } catch (error) {
      console.error('Failed to load campaigns:', error);
    }
  };

  useEffect(() => {
    loadConversations();
    loadCampaigns();
  }, [campaignFilter]);

  useEffect(() => {
    loadCampaigns();
  }, []);

  const filteredConversations = conversations.filter(conversation => {
    const matchesSearch = (conversation.contact?.name || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
                         (conversation.contact?.email || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
                         (conversation.lastMessage?.content || '').toLowerCase().includes(searchQuery.toLowerCase());
    const matchesStatus = statusFilter === 'All' || conversation.status === statusFilter.toLowerCase().replace(' ', '-');
    
    return matchesSearch && matchesStatus;
  });

  const statusCounts = {
    All: conversations.length,
    Active: conversations.filter(c => c.status === 'active').length,
    'High Priority': conversations.filter(c => c.status === 'high-priority').length,
    Positive: conversations.filter(c => c.status === 'positive').length,
    'Needs Response': conversations.filter(c => c.status === 'needs-response').length,
  };

  // Set up keyboard shortcuts for the new design system
  useConversationShortcuts({
    onSearchFocus: () => {
      const searchInput = document.querySelector('[data-search-input]') as HTMLInputElement;
      if (searchInput) searchInput.focus();
    },
    onNextConversation: () => {
      if (conversations.length > 0) {
        const currentIndex = selectedConversation 
          ? conversations.findIndex(c => c.id === selectedConversation.id)
          : -1;
        const nextIndex = (currentIndex + 1) % conversations.length;
        setSelectedConversation(conversations[nextIndex]);
      }
    },
    onPrevConversation: () => {
      if (conversations.length > 0) {
        const currentIndex = selectedConversation 
          ? conversations.findIndex(c => c.id === selectedConversation.id)
          : -1;
        const prevIndex = currentIndex <= 0 ? conversations.length - 1 : currentIndex - 1;
        setSelectedConversation(conversations[prevIndex]);
      }
    },
    onStarConversation: () => {
      if (selectedConversation) {
        console.log('Toggle star for conversation:', selectedConversation.id);
      }
    },
    onRefresh: () => {
      loadConversations();
    }
  });

  // Revolutionary design system implementation
  const LeftPanelContent = () => (
    <ConversationPanel>
      <PanelHeader>
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-semibold text-neutral-900">Conversations</h2>
          <Button size="sm" variant="outline">
            <Plus className="w-4 h-4" />
          </Button>
        </div>
      </PanelHeader>
      <PanelContent>
        <div className="p-4 space-y-4">
          {/* Search */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-neutral-400" />
            <input
              data-search-input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search conversations..."
              className="w-full pl-9 pr-4 py-2 bg-neutral-50 border border-neutral-200 rounded-lg text-neutral-900 placeholder-neutral-400 focus:outline-none focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500"
            />
          </div>
          
          {/* Conversation Cards using new design system */}
          <div className="space-y-2 overflow-y-auto">
            {filteredConversations.map((conversation) => {
              // Convert legacy conversation to new format
              const newFormatConversation = {
                ...conversation,
                status: conversation.status === 'high-priority' ? 'priority' as const :
                       conversation.status === 'positive' ? 'active' as const :
                       conversation.status === 'needs-response' ? 'escalated' as const :
                       conversation.status as 'active' | 'priority' | 'escalated' | 'ai-processing' | 'neutral',
                channel: 'email' as const,
                tags: conversation.labels,
                isStarred: false
              };
              
              return (
                <NewConversationCard
                  key={conversation.id}
                  conversation={newFormatConversation}
                  isSelected={selectedConversation?.id === conversation.id}
                  onClick={() => setSelectedConversation(conversation)}
                  onStarToggle={(id) => console.log('Star toggled:', id)}
                />
              );
            })}
          </div>
        </div>
      </PanelContent>
    </ConversationPanel>
  );

  const CenterPanelContent = () => (
    <ConversationPanel>
      <PanelHeader>
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-semibold text-neutral-900">
            {selectedConversation ? selectedConversation.contact.name : 'Select a conversation'}
          </h2>
          <div className="flex space-x-2">
            <Button size="sm" variant="outline">
              <Star className="w-4 h-4" />
            </Button>
            <Button size="sm" variant="outline">
              <Archive className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </PanelHeader>
      <PanelContent>
        {selectedConversation ? (
          <ConversationDetailView 
            conversation={selectedConversation}
            onRevolutionaryClick={() => setShowRevolutionaryMode(true)}
          />
        ) : (
          <div className="h-full flex items-center justify-center">
            <div className="text-center">
              <MessageSquare className="w-16 h-16 text-neutral-400 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-neutral-900 mb-2">Select a conversation</h3>
              <p className="text-neutral-600">
                Choose a conversation from the list to view messages
              </p>
            </div>
          </div>
        )}
      </PanelContent>
    </ConversationPanel>
  );

  const RightPanelContent = () => (
    <ConversationPanel>
      <PanelHeader>
        <h2 className="text-lg font-semibold text-neutral-900">Intelligence Hub</h2>
      </PanelHeader>
      <PanelContent>
        <div className="p-4 space-y-6">
          {/* Stats Cards using new design system */}
          <div className="space-y-4">
            <h3 className="text-sm font-medium text-neutral-700">Quick Stats</h3>
            <div className="grid grid-cols-1 gap-3">
              <div className="p-3 bg-primary-50 rounded-lg border border-primary-200">
                <div className="text-sm text-primary-600">Total</div>
                <div className="text-2xl font-bold text-primary-900">{stats.totalConversations}</div>
              </div>
              <div className="p-3 bg-green-50 rounded-lg border border-green-200">
                <div className="text-sm text-green-600">Active</div>
                <div className="text-2xl font-bold text-green-900">{stats.activeConversations}</div>
              </div>
              <div className="p-3 bg-orange-50 rounded-lg border border-orange-200">
                <div className="text-sm text-orange-600">High Priority</div>
                <div className="text-2xl font-bold text-orange-900">{stats.highPriority}</div>
              </div>
            </div>
          </div>
          
          {/* AI Insights */}
          <div className="space-y-4">
            <h3 className="text-sm font-medium text-neutral-700">AI Insights</h3>
            <div className="p-4 bg-purple-50 rounded-lg border border-purple-200">
              <div className="text-sm text-purple-600 mb-2">Conversation Sentiment</div>
              <div className="text-lg font-semibold text-purple-900">Positive</div>
              <div className="text-xs text-purple-600 mt-1">85% confidence</div>
            </div>
          </div>
        </div>
      </PanelContent>
    </ConversationPanel>
  );

  // Conditional rendering based on feature flag
  if (isRevolutionaryEnabled) {
    return (
      <div className="h-screen bg-neutral-50 p-6">
        {/* Revolutionary Design System Layout */}
        <ConversationLayout
          leftPanel={<LeftPanelContent />}
          centerPanel={<CenterPanelContent />}
          rightPanel={<RightPanelContent />}
          className="h-full"
        />
      </div>
    );
  }

  // Original/Legacy Interface
  return (
    <div className="min-h-screen bg-neutral-950">
      <div className="relative max-w-7xl mx-auto px-6 py-8">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="flex items-center justify-between mb-8"
        >
          <div>
            <h1 className="text-4xl font-bold bg-gradient-to-r from-blue-400 to-purple-500 bg-clip-text text-transparent mb-2">
              Conversations
            </h1>
            <p className="text-xl text-neutral-400">
              Manage and track all your outreach conversations
            </p>
          </div>
          <div className="flex items-center space-x-3">
            {/* Real-time indicator */}
            <div className="flex items-center space-x-2 px-3 py-2 bg-green-500/10 border border-green-500/30 rounded-lg">
              <div className="w-2 h-2 bg-green-400 rounded-full animate-pulse"></div>
              <span className="text-green-400 text-sm">Live</span>
            </div>

            {/* Command Palette Trigger */}
            <Button 
              onClick={() => setShowCommandPalette(true)}
              variant="outline"
              size="lg"
              className="border-neutral-600 text-neutral-300 hover:bg-neutral-800"
            >
              <MessageSquare className="w-5 h-5 mr-2" />
              Search
              <kbd className="ml-2 px-2 py-1 bg-neutral-700 text-neutral-300 text-xs rounded">⌘K</kbd>
            </Button>

            {/* Bulk Actions Toggle */}
            <Button 
              onClick={() => setBulkSelectMode(!bulkSelectMode)}
              variant="outline"
              size="lg"
              className={cn(
                "border-neutral-600 text-neutral-300 hover:bg-neutral-800",
                bulkSelectMode && "bg-blue-500/20 border-blue-500/50 text-blue-300"
              )}
            >
              <Users className="w-5 h-5 mr-2" />
              {bulkSelectMode ? 'Exit Bulk' : 'Bulk Actions'}
            </Button>
            
            <Button 
              variant="outline"
              size="lg"
              className="border-neutral-600 text-neutral-300 hover:bg-neutral-800"
            >
              <BarChart3 className="w-5 h-5 mr-2" />
              Analytics
            </Button>
            
            <Button 
              onClick={() => navigate('/conversations/management')}
              variant="outline"
              size="lg"
              className="border-blue-500/50 text-blue-400 hover:bg-blue-500/10 hover:border-blue-500 transition-all duration-300"
            >
              <Brain className="w-5 h-5 mr-2" />
              AI Management
            </Button>
            
            <Button 
              size="lg"
              className="bg-gradient-to-r from-blue-500 to-purple-600 hover:from-blue-600 hover:to-purple-700 shadow-xl hover:shadow-2xl transform hover:scale-105 transition-all duration-300"
            >
              <Plus className="w-5 h-5 mr-2" />
              New Message
            </Button>
          </div>
        </motion.div>

        {/* Stats Cards */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8"
        >
          <MetricCard
            title="Total Conversations"
            value={stats.totalConversations}
            change="+15% this week"
            icon={MessageSquare}
            gradient="bg-gradient-to-r from-blue-500 to-cyan-500"
            description="All active conversations"
          />
          <MetricCard
            title="Active Conversations"
            value={stats.activeConversations}
            change="+8% vs last week"
            icon={Activity}
            gradient="bg-gradient-to-r from-green-500 to-emerald-500"
            description="Currently ongoing"
          />
          <MetricCard
            title="High Priority"
            value={stats.highPriority}
            change="2 new today"
            icon={AlertCircle}
            gradient="bg-gradient-to-r from-red-500 to-orange-500"
            description="Requires immediate attention"
          />
          <MetricCard
            title="Avg Response Time"
            value={stats.avgResponseTime}
            change="-12% improvement"
            icon={Clock}
            gradient="bg-gradient-to-r from-purple-500 to-pink-500"
            description="Average time to respond"
          />
        </motion.div>

        {/* Main Content */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="grid grid-cols-1 lg:grid-cols-3 gap-6 h-[calc(100vh-400px)]"
        >
          {/* Conversations List */}
          <div className="lg:col-span-1 bg-gradient-to-br from-neutral-900/80 to-neutral-800/50 backdrop-blur-xl border border-neutral-700/50 rounded-2xl shadow-xl overflow-hidden">
        {/* Search and Filters */}
            <div className="p-4 border-b border-neutral-700/50">
              <div className="relative mb-4">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-neutral-400" />
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search conversations..."
                  className="w-full pl-9 pr-4 py-2 bg-neutral-800/50 border border-neutral-700 rounded-lg text-white placeholder-neutral-400 focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-transparent transition-all text-sm"
                />
              </div>

              {/* Campaign Filter */}
              <div className="mb-4">
                <label className="block text-sm font-medium text-neutral-300 mb-2">Campaign</label>
                <select
                  value={campaignFilter}
                  onChange={(e) => setCampaignFilter(e.target.value)}
                  className="w-full px-3 py-2 bg-neutral-800/50 border border-neutral-700 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-transparent text-sm"
                >
                  <option value="All">All Campaigns</option>
                  {campaigns.map((campaign) => (
                    <option key={campaign.id} value={campaign.id}>
                      {campaign.name}
                    </option>
                  ))}
                </select>
              </div>

              {/* Status Filter Tabs */}
              <div className="flex flex-wrap gap-2">
                {Object.entries(statusCounts).map(([status, count]) => (
                  <button
                    key={status}
                    onClick={() => setStatusFilter(status)}
                    className={cn(
                      'px-3 py-1.5 text-xs font-medium rounded-lg transition-all duration-200',
                      statusFilter === status
                        ? 'bg-blue-500 text-white shadow-lg'
                        : 'text-neutral-400 hover:text-white hover:bg-neutral-700/50'
                    )}
                  >
                    {status}
                    {count > 0 && (
                      <span className="ml-1 text-xs bg-neutral-600 text-neutral-300 px-1 py-0.5 rounded-full">
                        {count}
                      </span>
                    )}
                  </button>
                ))}
              </div>
            </div>
          
          {/* Conversations List */}
            <div className="overflow-y-auto h-full">
              {loading ? (
                <div className="flex items-center justify-center py-12">
                  <div className="text-center">
                    <div className="w-8 h-8 border-4 border-blue-500/30 border-t-blue-500 rounded-full animate-spin mx-auto mb-3"></div>
                    <p className="text-neutral-400 text-sm">Loading conversations...</p>
                  </div>
                </div>
              ) : filteredConversations.length === 0 ? (
                <div className="text-center py-12">
                  <MessageSquare className="w-12 h-12 text-neutral-600 mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-white mb-2">No conversations found</h3>
                  <p className="text-neutral-400 text-sm">
                    {searchQuery ? 'Try adjusting your search criteria' : 'Start by creating your first outreach campaign'}
                  </p>
                </div>
              ) : (
                filteredConversations.map((conversation) => (
                  <ConversationCard
                    key={conversation.id}
                    conversation={conversation}
                    isSelected={selectedConversation?.id === conversation.id}
                    onClick={() => setSelectedConversation(conversation)}
                    onRevolutionaryClick={() => {
                      setSelectedConversation(conversation);
                      setShowRevolutionaryMode(true);
                    }}
                    bulkSelectMode={bulkSelectMode}
                    isBulkSelected={selectedConversations.has(conversation.id)}
                    onBulkSelect={(id, selected) => {
                      const newSelected = new Set(selectedConversations);
                      if (selected) {
                        newSelected.add(id);
                      } else {
                        newSelected.delete(id);
                      }
                      setSelectedConversations(newSelected);
                    }}
                  />
                ))
              )}
            </div>
        </div>

                    {/* Conversation Detail */}
          <div className="lg:col-span-2 bg-gradient-to-br from-neutral-900/80 to-neutral-800/50 backdrop-blur-xl border border-neutral-700/50 rounded-2xl shadow-xl overflow-hidden">
          {selectedConversation ? (
              <ConversationDetailView 
                conversation={selectedConversation}
                onRevolutionaryClick={() => setShowRevolutionaryMode(true)}
              />
          ) : (
              <div className="h-full flex items-center justify-center">
                <div className="text-center">
                  <div className="w-16 h-16 bg-gradient-to-r from-blue-500 to-purple-600 rounded-2xl flex items-center justify-center mx-auto mb-4">
                    <MessageSquare className="w-8 h-8 text-white" />
                  </div>
                  <h3 className="text-xl font-semibold text-white mb-2">Select a conversation</h3>
                  <p className="text-neutral-400">
                    Choose a conversation from the list to view messages and continue the dialogue
                  </p>
                </div>
              </div>
          )}
          </div>
        </motion.div>

        {/* Command Palette */}
        <AnimatePresence>
        {showCommandPalette && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-start justify-center pt-32 z-50"
            onClick={() => setShowCommandPalette(false)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="bg-gradient-to-br from-neutral-900 to-neutral-800 border border-neutral-700 rounded-2xl shadow-2xl w-full max-w-2xl mx-4 overflow-hidden"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="p-6">
                <div className="flex items-center space-x-3 mb-6">
                  <div className="w-8 h-8 bg-gradient-to-r from-blue-500 to-purple-600 rounded-lg flex items-center justify-center">
                    <MessageSquare className="w-4 h-4 text-white" />
                  </div>
                  <input
                    type="text"
                    placeholder="Search conversations, commands, or contacts..."
                    className="flex-1 bg-transparent text-white text-lg placeholder-neutral-400 focus:outline-none"
                    autoFocus
                  />
                  <kbd className="px-2 py-1 bg-neutral-700 text-neutral-300 text-xs rounded">ESC</kbd>
                </div>
                
                <div className="space-y-2">
                  <div className="text-sm text-neutral-400 mb-2">Quick Actions</div>
                  {[
                    { icon: Sparkles, label: 'Generate AI Response', shortcut: '⌘ + G' },
                    { icon: Plus, label: 'New Conversation', shortcut: '⌘ + N' },
                    { icon: Users, label: 'Assign to Team Member', shortcut: '⌘ + A' },
                    { icon: Archive, label: 'Archive Conversation', shortcut: '⌘ + D' },
                    { icon: Brain, label: 'AI Management Dashboard', shortcut: '⌘ + M' }
                  ].map((action, index) => (
                    <div
                      key={index}
                      className="flex items-center justify-between p-3 rounded-lg hover:bg-neutral-700/50 cursor-pointer transition-colors"
                    >
                      <div className="flex items-center space-x-3">
                        <action.icon className="w-4 h-4 text-neutral-400" />
                        <span className="text-white">{action.label}</span>
                      </div>
                      <kbd className="px-2 py-1 bg-neutral-700 text-neutral-300 text-xs rounded">{action.shortcut}</kbd>
                    </div>
                  ))}
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Bulk Actions Bar */}
      <AnimatePresence>
        {bulkSelectMode && selectedConversations.size > 0 && (
          <motion.div
            initial={{ y: 100, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: 100, opacity: 0 }}
            className="fixed bottom-6 left-1/2 transform -translate-x-1/2 z-40"
          >
            <div className="bg-gradient-to-r from-blue-500 to-purple-600 rounded-2xl shadow-2xl p-4 flex items-center space-x-4">
              <span className="text-white font-medium">
                {selectedConversations.size} conversation{selectedConversations.size > 1 ? 's' : ''} selected
              </span>
              <div className="flex space-x-2">
                <button className="px-4 py-2 bg-white/20 hover:bg-white/30 text-white rounded-lg transition-colors">
                  Archive
                </button>
                <button className="px-4 py-2 bg-white/20 hover:bg-white/30 text-white rounded-lg transition-colors">
                  Assign
                </button>
                <button className="px-4 py-2 bg-white/20 hover:bg-white/30 text-white rounded-lg transition-colors">
                  Tag
                </button>
                <button 
                  onClick={() => {
                    setBulkSelectMode(false);
                    setSelectedConversations(new Set());
                  }}
                  className="px-4 py-2 bg-red-500/80 hover:bg-red-500 text-white rounded-lg transition-colors"
                >
                  Cancel
                </button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Full-Screen Revolutionary Mode */}
      <AnimatePresence>
        {showRevolutionaryMode && selectedConversation && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-neutral-950 z-50 overflow-hidden"
          >
            <RevolutionaryConversationView 
              conversationId={selectedConversation.id} 
              onBack={() => setShowRevolutionaryMode(false)}
            />
          </motion.div>
        )}
        </AnimatePresence>
      </div>
    </div>
  );
}; 